import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ResponseDtoService } from '@app/shared/services/response-dto.service';
import {
  MixerConfigAccessControl,
  MixerConfigOptions,
  PlcMixDesigns,
  ShadowUpdateAccepted,
  ThingDocument,
  ThingShadowRequest,
} from '@cco/model';
import { Observable } from 'rxjs';
import { APP_CONFIG, AppConfig } from '@cco/apps/cco-frontend';
import { switchMap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

@Injectable()
export class MixerPlcService extends ResponseDtoService {
  private readonly relativeUrl: string;

  constructor(@Inject(APP_CONFIG) config: AppConfig, protected http: HttpClient) {
    super(config.apiBaseUrl);
    this.relativeUrl = `/mixers`;
  }

  getIotGatewayThingDocument(assetUuid: string): Observable<ThingDocument> {
    if (assetUuid == null)
      Sentry.captureException(new Error('assetUuid was undefined or null'));
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument>(`${baseUrl + this.relativeUrl}/${assetUuid}/iot-gateway/thing-document`)));
  }

  getPlcGatewayThingDocument(assetUuid: string): Observable<ThingDocument> {
    if (assetUuid == null)
      Sentry.captureException(new Error('assetUuid was undefined or null'));
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument>(`${baseUrl + this.relativeUrl}/${assetUuid}/plc-gateway/shadow`)));
  }

  getPlcConfigThingDocument(assetUuid: string): Observable<ThingDocument> {
    if (assetUuid == null)
      Sentry.captureException(new Error('assetUuid was undefined or null'));
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument>(`${baseUrl + this.relativeUrl}/${assetUuid}/plc/config`)));
  }

  getPlcCalibrationThingDocument(assetUuid: string): Observable<ThingDocument> {
    if (assetUuid == null)
      Sentry.captureException(new Error('assetUuid was undefined or null'));
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument>(`${baseUrl + this.relativeUrl}/${assetUuid}/plc/calibration`)));
  }

  getPlcMixDesignThingDocument(
    assetUuid: string,
    mixDesignSlot: string,
  ): Observable<ThingDocument> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument>(
      `${baseUrl + this.relativeUrl}/${assetUuid}/plc/mix-designs/${mixDesignSlot}`,
    )));
  }

  updateConfigurationAccessControl$(assetUuid: string, accessControl: MixerConfigAccessControl): Observable<string> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.put(
      `${baseUrl + this.relativeUrl}/${assetUuid}/cco-plc-mixer-config-v1/access-control`,
      accessControl,
      {
        responseType: 'text'
      }
    )));
  }

  updateConfigurationOptions$(assetUuid: string, options: MixerConfigOptions): Observable<string> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.put(
      `${baseUrl + this.relativeUrl}/${assetUuid}/cco-plc-mixer-config-v1/options`,
      options,
      {
        responseType: 'text'
      }
    )));
  }

  updateMixDesign(
    assetUuid: string,
    mixDesignSlot: string,
    shadowUpdate: ThingShadowRequest<PlcMixDesigns>,
  ): Observable<string> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.put(
      `${baseUrl + this.relativeUrl}/${assetUuid}/plc/mix-designs/${mixDesignSlot}`,
      shadowUpdate,
      {
        responseType: 'text',
      },
    )));
  }

  getPlcMixDesignThingDocuments(assetUuid: string): Observable<ThingDocument[]> {
    return this.tenantBaseUrl$.pipe(switchMap(baseUrl => this.http.get<ThingDocument[]>(`${baseUrl + this.relativeUrl}/${assetUuid}/plc/mix-design-thing-documents`)));
  }

  public mapUpdateMixDesignShadowAcceptedResponse(
    shadowUpdate: string,
  ): ShadowUpdateAccepted<PlcMixDesigns> {
    return JSON.parse(shadowUpdate);
  }
}
